import { useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';

import selectors from '@/redux/selectors/selectors';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import Button from '@/components/Button';
import FloatingSelectV2 from '@/components/v2/FloatingSelectV2';
import useWorkflowLogsFilters from '@/components/modals/workflowLogsModal/lib/useWorkflowLogsFilters';
import ensureSelectedFilterOptions from '@/components/modals/workflowLogsModal/lib/ensureSelectedFilterOptions';
import { FiMoreHorizontal, FiUser, LuContainer } from '@/components/react-icons/main/index.esm';

const AdditionalFiltersButton = () => {
  const workflowId = useArgSelector(selectors.workflowId);
  const endUserFilter = useArgSelector(selectors.pref, `workflowLogsEndUserFilter-${workflowId}`);
  const installationFilter = useArgSelector(selectors.pref, `workflowLogsInstallationFilter-${workflowId}`);
  const workflowLogsMetadataEndUsers = useSelector(selectors.workflowLogsMetadataEndUsers);
  const workflowLogsMetadataInstallations = useSelector(selectors.workflowLogsMetadataInstallations);
  const activeCompany = useSelector(selectors.activeCompany);
  const enableEmbedded = activeCompany?.settings?.enableEmbedded;
  const { handleFilterChange } = useWorkflowLogsFilters();

  const hasActiveFilters = endUserFilter?.length > 0 || installationFilter?.length > 0;

  const shouldShowButton = enableEmbedded && (workflowLogsMetadataEndUsers?.length > 0 || workflowLogsMetadataInstallations?.length > 0 || hasActiveFilters);

  const handleEndUserFilterChange = useCallback(
    (value) => {
      handleFilterChange('endUser', value?.map((item) => item.replace('endUser:', '')) || []);
    },
    [handleFilterChange]
  );

  const handleInstallationFilterChange = useCallback(
    (value) => {
      handleFilterChange('installation', value?.map((item) => item.replace('installation:', '')) || []);
    },
    [handleFilterChange]
  );

  const endUserOptions = useMemo(() => {
    const options = workflowLogsMetadataEndUsers
      ?.map((metadata) => ({
        label: metadata?.label,
        subLabel: metadata?.label === metadata?.id ? undefined : metadata?.id,
        searchText: `${metadata?.label || ''} ${metadata?.id || ''}`,
        value: `endUser:${metadata?.id}`,
        totalCount: metadata?.totalCount,
        onChange: handleEndUserFilterChange
      }))
      .filter(Boolean)
      .sort((a, b) => b.totalCount - a.totalCount);

    return ensureSelectedFilterOptions({
      options,
      selectedValues: endUserFilter?.map((value) => `endUser:${value}`),
      getSelectedOption: (value) => ({ label: value.replace('endUser:', '') })
    });
  }, [workflowLogsMetadataEndUsers, endUserFilter, handleEndUserFilterChange]);

  const installationOptions = useMemo(() => {
    const options = workflowLogsMetadataInstallations
      ?.map((metadata) => ({
        label: metadata?.label,
        subLabel: metadata?.label === metadata?.id ? undefined : metadata?.id,
        searchText: `${metadata?.label || ''} ${metadata?.id || ''}`,
        value: `installation:${metadata?.id}`,
        totalCount: metadata?.totalCount,
        onChange: handleInstallationFilterChange
      }))
      .filter(Boolean)
      .sort((a, b) => b.totalCount - a.totalCount);

    return ensureSelectedFilterOptions({
      options,
      selectedValues: installationFilter?.map((value) => `installation:${value}`),
      getSelectedOption: (value) => ({ label: value.replace('installation:', '') })
    });
  }, [workflowLogsMetadataInstallations, installationFilter, handleInstallationFilterChange]);

  const selectedValues = useMemo(() => [...(endUserFilter || []).map((value) => `endUser:${value}`), ...(installationFilter || []).map((value) => `installation:${value}`)], [endUserFilter, installationFilter]);

  // Create filter options that look like normal FloatingSelectV2 options
  const filterOptions = [
    {
      value: 'endUser',
      label: 'End Users',
      icon: FiUser,
      onChange: () => {},
      children: endUserOptions
    },
    {
      value: 'installation',
      label: 'Installations',
      icon: LuContainer,
      onChange: () => {},
      children: installationOptions
    }
  ].filter((option) => option.children?.length > 0);

  if (!shouldShowButton) {
    return null;
  }

  return (
    <>
      <Button className={`outline filter additional-filters-button ${hasActiveFilters ? 'active' : ''}`} size="sm" icon={FiMoreHorizontal} title="Additional Filters" />
      <FloatingSelectV2 triggers={[`.additional-filters-button`]} options={filterOptions} placement="bottom" offset={4} useSearch={false} onChange={() => {}} multiple value={selectedValues} hideCheckboxes optionRowHeight={46} />
    </>
  );
};

export default AdditionalFiltersButton;
